import { z } from 'zod';

/**
 * Environment variable schema with validation rules
 */
const envSchema = z.object({
  // API Configuration
  VITE_API_URL: z.string().url('VITE_API_URL must be a valid URL'),
  VITE_ADMIN_API_URL: z.string().url('VITE_ADMIN_API_URL must be a valid URL').optional(),

  // Cognito Configuration (Audit Log Admin Pool)
  VITE_AUDITLOG_COGNITO_USER_POOL_ID: z
    .string()
    .min(1, 'VITE_AUDITLOG_COGNITO_USER_POOL_ID is required'),
  VITE_AUDITLOG_COGNITO_CLIENT_ID: z.string().min(1, 'VITE_AUDITLOG_COGNITO_CLIENT_ID is required'),
  VITE_AWS_REGION: z.string().min(1, 'VITE_AWS_REGION is required').default('us-east-1'),

  // Optional: Feature Flags
  VITE_ENABLE_ANALYTICS: z
    .string()
    .transform(val => val === 'true')
    .default('true'),
  VITE_ENABLE_EXPORT: z
    .string()
    .transform(val => val === 'true')
    .default('true'),
  VITE_AUTO_REFRESH_INTERVAL: z
    .string()
    .transform(val => parseInt(val, 10))
    .default('300000'), // 5 minutes
});

/**
 * Validated and type-safe environment variables
 */
export type Env = z.infer<typeof envSchema>;

/**
 * Validates environment variables and returns typed config
 * @throws {Error} If required environment variables are missing or invalid
 */
function validateEnv(): Env {
  try {
    return envSchema.parse({
      VITE_API_URL: import.meta.env.VITE_API_URL,
      VITE_ADMIN_API_URL: import.meta.env.VITE_ADMIN_API_URL,
      VITE_AUDITLOG_COGNITO_USER_POOL_ID: import.meta.env.VITE_AUDITLOG_COGNITO_USER_POOL_ID,
      VITE_AUDITLOG_COGNITO_CLIENT_ID: import.meta.env.VITE_AUDITLOG_COGNITO_CLIENT_ID,
      VITE_AWS_REGION: import.meta.env.VITE_AWS_REGION,
      VITE_ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS,
      VITE_ENABLE_EXPORT: import.meta.env.VITE_ENABLE_EXPORT,
      VITE_AUTO_REFRESH_INTERVAL: import.meta.env.VITE_AUTO_REFRESH_INTERVAL,
    });
  } catch (error) {
    if (error instanceof z.ZodError) {
      const missingVars = error.errors.map(err => `${err.path.join('.')}: ${err.message}`);
      throw new Error(
        `Environment validation failed:\n${missingVars.join('\n')}\n\nPlease check your .env file and ensure all required variables are set.`
      );
    }
    throw error;
  }
}

/**
 * Exported environment configuration object
 * Validated at app startup
 */
export const env = validateEnv();

/**
 * Type-safe environment variable access
 */
export const config = {
  api: {
    baseUrl: env.VITE_API_URL,
    adminBaseUrl: env.VITE_ADMIN_API_URL ?? env.VITE_API_URL,
  },
  cognito: {
    userPoolId: env.VITE_AUDITLOG_COGNITO_USER_POOL_ID,
    clientId: env.VITE_AUDITLOG_COGNITO_CLIENT_ID,
    region: env.VITE_AWS_REGION,
  },
  features: {
    enableAnalytics: env.VITE_ENABLE_ANALYTICS,
    enableExport: env.VITE_ENABLE_EXPORT,
    autoRefreshInterval: env.VITE_AUTO_REFRESH_INTERVAL,
  },
} as const;
